import { cn } from "@/lib/cn";
import { discountPercent } from "@/lib/product-sale";
import { formatPrice } from "@/lib/format";

export interface PriceProps {
  cents: number;
  /** The pre-discount price, when the product is on sale. Ignored unless above `cents`. */
  compareAtCents?: number;
  size?: "sm" | "card" | "md" | "lg";
}

const SIZE_CLASSES = { sm: "text-small", card: "text-body", md: "text-h4", lg: "text-h3" } as const;
const COMPARE_SIZE_CLASSES = { sm: "text-caption", card: "text-small", md: "text-body", lg: "text-body" } as const;

export function Price({ cents, compareAtCents, size = "md" }: PriceProps) {
  const onSale = compareAtCents !== undefined && compareAtCents > cents;
  const amount = (
    <span className={cn("tnum font-semibold", cents === 0 ? "text-accent" : "text-text-primary", SIZE_CLASSES[size])}>
      {formatPrice(cents)}
    </span>
  );
  if (!onSale) return amount;

  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      {amount}
      {/* `<s>` carries the "no longer applies" meaning; the label names what the number is. */}
      <s className={cn("tnum text-text-muted", COMPARE_SIZE_CLASSES[size])}>
        <span className="sr-only">Regular price </span>
        {formatPrice(compareAtCents)}
      </s>
      <span className="rounded-xs bg-accent-soft px-1.5 py-0.5 text-caption font-semibold text-accent tnum">
        −{discountPercent(cents, compareAtCents)}%
      </span>
    </span>
  );
}
